import React from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { IMG } from '../i18n/translations';
import { Reveal } from '../components/Reveal';
import { CTABand } from '../components/CTABand';

export default function About() {
  const { t } = useLanguage();
  const a = t.about;

  return (
    <div data-testid="page-about">
      {/* Hero */}
      <section className="px-6 lg:px-12 pt-32 md:pt-40 pb-16">
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-12 gap-12">
          <div className="md:col-span-2">
            <Reveal>
              <p className="overline">{a.overline}</p>
            </Reveal>
          </div>
          <div className="md:col-span-10">
            <Reveal delay={0.05}>
              <h1
                className="font-serif font-light text-[2.6rem] md:text-[5.4rem] leading-[1] tracking-tight"
                style={{ color: 'var(--ink)' }}
              >
                {a.title}
              </h1>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Lead + first image */}
      <section className="px-6 lg:px-12 pb-20">
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-7">
            <Reveal>
              <img
                src={IMG.aboutTeam1}
                alt={a.teamCaption}
                className="w-full aspect-[4/3] object-cover"
              />
              <p className="mt-3 text-[12px] italic" style={{ color: 'var(--ink-3)' }}>
                {a.teamCaption}
              </p>
            </Reveal>
          </div>
          <div className="md:col-span-5 md:pt-4">
            <Reveal delay={0.08}>
              <p className="font-serif italic text-2xl md:text-[1.8rem] leading-[1.3]" style={{ color: 'var(--ink)' }}>
                {a.lead}
              </p>
            </Reveal>
            <Reveal delay={0.14}>
              <p className="mt-8 text-[16px] leading-relaxed" style={{ color: 'var(--ink-2)' }}>
                {a.body1}
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-5 text-[16px] leading-relaxed" style={{ color: 'var(--ink-2)' }}>
                {a.body2}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Second image */}
      <section className="px-6 lg:px-12 pb-24">
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <img
              src={IMG.aboutTeam2}
              alt={a.teamCaption2}
              className="w-full aspect-[16/8] object-cover"
            />
            <p className="mt-3 text-[12px] italic" style={{ color: 'var(--ink-3)' }}>
              {a.teamCaption2}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Our way */}
      <section
        className="px-6 lg:px-12 py-24"
        style={{ background: 'var(--paper-2)', borderTop: '1px solid var(--rule)' }}
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-12 gap-12 mb-14">
            <div className="md:col-span-4">
              <Reveal>
                <p className="overline">{a.wayOverline}</p>
              </Reveal>
            </div>
            <div className="md:col-span-8">
              <Reveal delay={0.05}>
                <h2
                  className="font-serif text-3xl md:text-[2.7rem] font-light leading-tight tracking-tight"
                  style={{ color: 'var(--ink)' }}
                >
                  {a.wayTitle}
                </h2>
              </Reveal>
            </div>
          </div>

          <div className="grid md:grid-cols-2" style={{ borderTop: '1px solid var(--rule-strong)' }}>
            {a.way.map((v, i) => (
              <Reveal key={v.n} delay={(i % 2) * 0.06}>
                <div
                  className="p-10 md:p-12"
                  style={{
                    borderBottom: '1px solid var(--rule)',
                    borderRight: (i % 2 === 0) ? '1px solid var(--rule)' : 'none',
                  }}
                  data-testid={`value-${i}`}
                >
                  <span className="font-serif text-xl italic" style={{ color: 'var(--accent)' }}>
                    {v.n}
                  </span>
                  <h3 className="mt-4 font-serif text-3xl md:text-[2rem] leading-tight" style={{ color: 'var(--ink)' }}>
                    {v.title}
                  </h3>
                  <p className="mt-4 text-[15px] leading-relaxed max-w-md" style={{ color: 'var(--ink-2)' }}>
                    {v.description}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABand title={a.closingTitle} body={a.closingBody} testid="about-cta" />
    </div>
  );
}
